import { createApi } from '@reduxjs/toolkit/query/react';

import { ApiEndPointsResponseData } from 'models/api';
import {
  LoginResponseShape,
  UserAccountShape,
  UserAccountUpdateShape,
} from 'models/auth';
import { baseQuery } from '..';
import { ApiErrorResponse } from 'content/pages/Guest/InitializePassword/InitializePassword';
import { RequestResetPasswordResponse } from 'content/pages/Guest/RequestResetPassword/RequestResetPassword';

export type UserApiLoginQueryData = {
  url: string;
  userData: FormData;
};
export type InitializePasswordData = {
  body: { password: string; confirm_password: string };
  token: string;
  url: string;
};
export type RequestResetPasswordQuery = {
  email: string;
  url: string;
};
export type PostType = {
  url: string;
  payload: RequestResetPasswordQuery;
};

export const AuthApi = createApi({
  reducerPath: 'authApi',
  tagTypes: ['BaseLinks', 'Profile'],
  baseQuery,
  endpoints: (builder) => ({
    /**
     * Endpoint for getting all baseLinks
     */
    baseApiLinks: builder.query<ApiEndPointsResponseData, string | undefined>({
      query: (token) => {
        return {
          url: '/',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        };
      },
      providesTags: ['BaseLinks'],
    }),

    /**
     * Endpoint for getting user profile
     * (url for profile must be provided when making the query)
     */

    setPassword: builder.mutation<ApiErrorResponse, InitializePasswordData>({
      query: ({ url, body, token }) => ({
        url: url,
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: body,
      }),
    }),
    getProfile: builder.query<UserAccountShape, string>({
      query: (url) => ({
        url: url,
      }),
      providesTags: ['Profile'],
    }),
    updateProfile: builder.mutation<ApiErrorResponse, UserAccountUpdateShape>({
      query: ({ url, body }) => ({
        url: url,
        method: 'PATCH',
        body: body,
      }),
      invalidatesTags: ['Profile'],
    }),
    /**
     * Endpoint for logging in
     * (url for profile must be provided when making the query)
     */
    login: builder.mutation<LoginResponseShape, UserApiLoginQueryData>({
      query: ({ url, userData }) => ({
        url: url,
        method: 'POST',
        body: userData,
        formData: true,
      }),
      invalidatesTags: ['BaseLinks', 'Profile'],
    }),
    requestResetPassword: builder.mutation<
      RequestResetPasswordResponse,
      RequestResetPasswordQuery
    >({
      query: ({ url, email }) => ({
        url: url,
        method: 'POST',
        body: { email },
      }),
    }),
    /**
     * Endpoint for logging out
     * (url for profile must be provided when making the query)
     */
    logout: builder.mutation<void, string>({
      query: (url) => ({
        url: url,
        method: 'POST',
      }),
      invalidatesTags: ['BaseLinks'],
    }),
  }),
});

export const {
  useBaseApiLinksQuery,
  useGetProfileQuery,
  useLoginMutation,
  useLogoutMutation,
  useRequestResetPasswordMutation,
  useSetPasswordMutation,
  useUpdateProfileMutation,
} = AuthApi;
